import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';
import { EnhancedCharacter } from '../../../utils/ClassComponentRegistry';
import { loadClassById } from '../../../utils/classDataLoader';
import { EnhancedClass, Subclass } from '../../../types/class-data';
import { calculateTierFromLevel, isFeatureAvailable } from '../../../utils/levelTierMapping';
import CoreClassTab from './CoreClassTab';
import SubclassTab from './SubclassTab';
import KeyboardHints from './KeyboardHints';
import { TabType, FeatureData } from './types';
import styles from './ClassFeaturesPanel.module.css';

interface ClassFeaturesPanelProps {
  enhancedCharacter: EnhancedCharacter;
}

const ClassFeaturesPanel: React.FC<ClassFeaturesPanelProps> = ({ enhancedCharacter }) => {
  const [classData, setClassData] = useState<EnhancedClass | null>(null);
  const [currentSubclass, setCurrentSubclass] = useState<Subclass | null>(null);
  const [loading, setLoading] = useState(true);

  // UI State
  const [activeTab, setActiveTab] = useState<TabType>('class');
  const [expandedFeatures, setExpandedFeatures] = useState<Set<string>>(new Set());
  const [multiclassLoading, setMulticlassLoading] = useState(false);

  const character = enhancedCharacter.character;
  const characterLevel = character ? parseInt(character.level) || 1 : 1;
  const characterTier = calculateTierFromLevel(characterLevel);

  // Load class data
  useEffect(() => {
    const loadClassData = async () => {
      if (!character?.class) {
        setLoading(false);
        return;
      }

      try {
        const data = await loadClassById(character.class);
        setClassData(data);

        // Find current subclass if set
        if (data && character.subclass) {
          const subclass = data.subclasses.find(sub => sub.id === character.subclass);
          setCurrentSubclass(subclass || null);
        }
      } catch (error) {
        console.error('Failed to load class data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadClassData();
  }, [character?.class, character?.subclass]);

  // Load preferences from localStorage
  useEffect(() => {
    const savedTab = localStorage.getItem('classFeatures-activeTab') as TabType;

    if (savedTab) setActiveTab(savedTab);

    // Default tab based on tier
    if (!savedTab && characterTier >= 2) {
      setActiveTab('subclass');
    }
  }, [characterTier]);

  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem('classFeatures-activeTab', activeTab);
  }, [activeTab]);

  // Toggle feature expansion - useCallback for performance
  const handleToggleExpand = useCallback((featureId: string) => {
    setExpandedFeatures(prev => {
      const newSet = new Set(prev);
      if (newSet.has(featureId)) {
        newSet.delete(featureId);
      } else {
        newSet.add(featureId);
      }
      return newSet;
    });
  }, []);

  // Collapse all expanded features - for Escape key
  const collapseAll = useCallback(() => {
    setExpandedFeatures(new Set());
  }, []);

  // Tab navigation helpers
  const tabs: TabType[] = ['class', 'subclass'];

  const goToNextTab = () => {
    const currentIndex = tabs.indexOf(activeTab);
    const nextIndex = (currentIndex + 1) % tabs.length;
    setActiveTab(tabs[nextIndex]);
  };

  const goToPreviousTab = () => {
    const currentIndex = tabs.indexOf(activeTab);
    const previousIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    setActiveTab(tabs[previousIndex]);
  };

  // Swipe gesture handlers
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => goToNextTab(),
    onSwipedRight: () => goToPreviousTab(),
    trackMouse: false, // Only track touch events
    preventScrollOnSwipe: false,
    delta: 50 // Minimum swipe distance
  });

  // Multiclass data state (declared early so it can be used in useMemos below)
  const [multiclassClassFeature, setMulticlassClassFeature] = useState<FeatureData | null>(null);
  const [multiclassDomainAccess, setMulticlassDomainAccess] = useState<FeatureData | null>(null);
  const [multiclassFoundation, setMulticlassFoundation] = useState<FeatureData | null>(null);

  // Build feature data for each tab (will merge with multiclass features later)
  const { coreFeatures: baseCoreFeatures, subclassFeatures: baseSubclassFeatures } = useMemo(() => {
    const core: FeatureData[] = [];
    const subclass: FeatureData[] = [];

    if (!classData) return { coreFeatures: core, subclassFeatures: subclass };

    // Core Class Features
    const availableClassFeatures = classData.classFeatures?.filter(feature => {
      const featureTier = (feature as any).tier || 1;
      return isFeatureAvailable(featureTier, characterLevel);
    }) || [];

    availableClassFeatures.forEach((feature, index) => {
      core.push({
        id: `class-${index}`,
        name: feature.name,
        description: feature.description,
        type: 'class',
        className: classData.name,
        uses: feature.uses,
        progression: feature.progression
      });
    });

    // Hope Feature
    if (classData.hopeFeature) {
      core.push({
        id: 'hope-feature',
        name: classData.hopeFeature.name,
        description: classData.hopeFeature.description,
        type: 'hope',
        className: classData.name,
        cost: classData.hopeFeature.cost
      });
    }

    // Subclass Features
    if (currentSubclass && character) {
      const hasSpecialization = character.subclassCardTier === 'specialization' || character.subclassCardTier === 'mastery';
      const hasMastery = character.subclassCardTier === 'mastery';

      // Foundation (always available)
      if (Array.isArray(currentSubclass.foundationFeature)) {
        currentSubclass.foundationFeature.forEach((feature, idx) => {
          subclass.push({
            id: `subclass-foundation-${idx}`,
            name: feature.name,
            description: feature.description,
            type: 'subclass-foundation',
            className: classData.name,
            subclassName: currentSubclass.name
          });
        });
      }

      // Specialization (only if unlocked)
      if (hasSpecialization && Array.isArray(currentSubclass.specializationFeature)) {
        currentSubclass.specializationFeature.forEach((feature, idx) => {
          subclass.push({
            id: `subclass-specialization-${idx}`,
            name: feature.name,
            description: feature.description,
            type: 'subclass-specialization',
            className: classData.name,
            subclassName: currentSubclass.name
          });
        });
      }

      // Mastery (only if unlocked)
      if (hasMastery && Array.isArray(currentSubclass.masteryFeature)) {
        currentSubclass.masteryFeature.forEach((feature, idx) => {
          subclass.push({
            id: `subclass-mastery-${idx}`,
            name: feature.name,
            description: feature.description,
            type: 'subclass-mastery',
            className: classData.name,
            subclassName: currentSubclass.name
          });
        });
      }
    }

    return { coreFeatures: core, subclassFeatures: subclass };
  }, [classData, currentSubclass, character, characterLevel]);

  // Merge multiclass features with base features
  const coreFeatures = useMemo(() => {
    const features = [...baseCoreFeatures];
    if (multiclassClassFeature) {
      features.push(multiclassClassFeature);
    }
    if (multiclassDomainAccess) {
      features.push(multiclassDomainAccess);
    }
    return features;
  }, [baseCoreFeatures, multiclassClassFeature, multiclassDomainAccess]);

  const subclassFeatures = useMemo(() => {
    const features = [...baseSubclassFeatures];
    if (multiclassFoundation) {
      features.push(multiclassFoundation);
    }
    return features;
  }, [baseSubclassFeatures, multiclassFoundation]);

  // Load multiclass data separately with async loader
  useEffect(() => {
    const loadMulticlassData = async () => {
      if (!character?.multiclass) {
        setMulticlassClassFeature(null);
        setMulticlassDomainAccess(null);
        setMulticlassFoundation(null);
        setMulticlassLoading(false);
        return;
      }

      const mc = character.multiclass;

      setMulticlassLoading(true);
      try {
        // Load multiclass class data
        const multiclassClassData = await loadClassById(mc.classId);
        if (!multiclassClassData) {
          console.error('Failed to load multiclass data for', mc.classId);
          return;
        }

        // Find the multiclass subclass
        const multiclassSubclass = multiclassClassData.subclasses.find(
          sub => sub.id === mc.subclassId
        );

        if (!multiclassSubclass) {
          console.error('Failed to find multiclass subclass', mc.subclassId);
          return;
        }

        // Calculate domain access level (based on character level)
        // Domain cards are available up to half the character level (rounded up)
        const currentAccessLevel = Math.ceil(characterLevel / 2);
        const availableLevels = [];
        for (let level = 1; level <= currentAccessLevel && level <= 3; level++) {
          availableLevels.push(level);
        }

        // Create shared multiclass data
        const sharedMulticlassData = {
          classId: mc.classId,
          className: mc.className,
          acquiredAtLevel: mc.acquiredAtLevel,
          classFeature: {
            name: mc.classFeature,
            description: mc.classFeatureDescription
          },
          foundationCard: {
            id: mc.foundationCardId,
            name: mc.foundationCardName,
            description: Array.isArray(multiclassSubclass.foundationFeature)
              ? multiclassSubclass.foundationFeature[0]?.description || ''
              : ''
          },
          domain: {
            id: mc.domainId,
            name: mc.domainName,
            availableTiers: availableLevels,
            currentAccessTier: currentAccessLevel
          },
          subclass: {
            id: mc.subclassId,
            name: mc.subclassName
          }
        };

        // Create multiclass class feature (goes in Class tab)
        const classFeature: FeatureData = {
          id: 'multiclass-class-feature',
          name: mc.classFeature,
          description: mc.classFeatureDescription,
          type: 'multiclass-class-feature',
          multiclassData: sharedMulticlassData
        };

        // Create multiclass domain access (goes in Class tab)
        const domainAccessDescription = `You have access to ${mc.domainName} domain cards from the Domain Cards section.`;
        const domainAccess: FeatureData = {
          id: 'multiclass-domain-access',
          name: `${mc.domainName} Domain Access`,
          description: domainAccessDescription,
          type: 'multiclass-domain-access',
          availableLevels: availableLevels,
          multiclassData: sharedMulticlassData
        };

        // Create multiclass foundation feature (goes in Subclass tab)
        const foundationFeature: FeatureData = {
          id: 'multiclass-foundation',
          name: mc.foundationCardName,
          description: Array.isArray(multiclassSubclass.foundationFeature)
            ? multiclassSubclass.foundationFeature[0]?.description || ''
            : '',
          type: 'multiclass-foundation',
          subclassName: mc.subclassName,
          multiclassData: sharedMulticlassData
        };

        setMulticlassClassFeature(classFeature);
        setMulticlassDomainAccess(domainAccess);
        setMulticlassFoundation(foundationFeature);
      } catch (error) {
        console.error('Error loading multiclass data:', error);
        setMulticlassClassFeature(null);
        setMulticlassDomainAccess(null);
        setMulticlassFoundation(null);
      } finally {
        setMulticlassLoading(false);
      }
    };

    loadMulticlassData();
  }, [character?.multiclass, characterLevel]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts if user is typing in an input/textarea
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      switch (event.key.toLowerCase()) {
        case '1':
          setActiveTab('class');
          break;
        case '2':
          setActiveTab('subclass');
          break;
        case 'escape':
          collapseAll();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [collapseAll]);

  // Early return if no character (after all hooks)
  if (!character) {
    return (
      <motion.div
        className={styles.panel}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className={styles.error}>
          No character data available.
        </div>
      </motion.div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <motion.div
        className={styles.panel}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className={styles.loading}>
          Loading class features...
        </div>
      </motion.div>
    );
  }

  // No class data
  if (!classData) {
    return (
      <motion.div
        className={styles.panel}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className={styles.error}>
          Class data not found for: {character.class}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className={styles.panel}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      {/* Header */}
      <div className={styles.header}>
        <h3 className={styles.title}>Class Features</h3>
        <div className={styles.headerActions}>
          <KeyboardHints />
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={styles.tabBar} role="tablist" aria-label="Class features sections">
        <button
          id="class-tab"
          className={`${styles.tab} ${activeTab === 'class' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('class')}
          role="tab"
          aria-selected={activeTab === 'class'}
          aria-controls="class-tab-panel"
          aria-label={`Class features (${coreFeatures.length} features)`}
        >
          Class
          <span className={styles.tabCount} aria-hidden="true">{coreFeatures.length}</span>
        </button>
        <button
          id="subclass-tab"
          className={`${styles.tab} ${activeTab === 'subclass' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('subclass')}
          role="tab"
          aria-selected={activeTab === 'subclass'}
          aria-controls="subclass-tab-panel"
          aria-label={`Subclass features (${subclassFeatures.length} features)`}
        >
          Subclass
          <span className={styles.tabCount} aria-hidden="true">{subclassFeatures.length}</span>
        </button>
      </div>

      {/* Tab Content */}
      <div className={styles.tabPanels} {...swipeHandlers}>
        {activeTab === 'class' && (
          <div
            role="tabpanel"
            id="class-tab-panel"
            aria-labelledby="class-tab"
            tabIndex={0}
          >
            <CoreClassTab
              features={coreFeatures}
              expandedFeatures={expandedFeatures}
              onToggleExpand={handleToggleExpand}
              isLoading={multiclassLoading}
            />
          </div>
        )}
        {activeTab === 'subclass' && (
          <div
            role="tabpanel"
            id="subclass-tab-panel"
            aria-labelledby="subclass-tab"
            tabIndex={0}
          >
            <SubclassTab
              features={subclassFeatures}
              expandedFeatures={expandedFeatures}
              onToggleExpand={handleToggleExpand}
              isLoading={multiclassLoading}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ClassFeaturesPanel;
